/** @jsxImportSource ./simboluri-jsx */
// PAGINA UNEI MĂRCI de bară / etrier («3g», «7gF», «e1», «v2»): ce e tipul
// (forma, Ø, dimensiunile, descrierea din registrul componentelor), forma
// lui desenată și cotată (aceeași `DrawingData` ca miniatura din hover) și
// UNDE SE FOLOSEȘTE — elementele care îl poartă, din extrasul de armătură,
// fiecare cu drum spre pagina lui.
//
// Rută: `/p/:id/structura/:sol/armatura/:marca` (v. `legaturi.hashMarca`).
// Tipul se caută în `componente.bare|etrieri` după `marci` / `marca`
// (numărul mărcii e contiguu pe familie, nu id-ul tipului); după numărul din
// marcă (= `tip`) numai când niciun tip al categoriei nu poartă o marcă — un
// extras vechi. Pozițiile extrasului se potrivesc pe `tip`-ul rândului găsit
// (toate utilizările tipului, în orice familie), altfel pe `marca` lor.

import { useEffect, useMemo, useState } from "react";
import { numar, useT } from "../i18n";
import {
  fetchDrawingUrl, getEstimare, getExtrasArmare, getPlanArmareColi, planArmareMarcaUrl,
} from "./apiStructura";
import { DrawingView } from "./DwgViewer";
import { hashComponente, hashElement, hashSectiune } from "./legaturi";
import { NUME_FAMILIE_MARCA, categoriaMarcii, citesteMarca } from "./marci";
import type { DrawingData, PozitieExtras, TipBara } from "./tipuri";

const nr = (v: number | null | undefined, zec = 0) =>
  v == null ? "—" : numar(v, { maximumFractionDigits: zec });

/** Rândul de tip al mărcii din registru: după `marci` (marca lui în orice
 *  familie), apoi după `marca`; după numărul mărcii (= id-ul tipului) numai
 *  când niciun tip al listei nu poartă o marcă. */
export function tipulMarcii(lista: TipBara[] | undefined, marca: string): TipBara | null {
  const c = citesteMarca(marca);
  if (!c || !lista) return null;
  const scris = lista.find((x) => x.marci?.includes(marca))
    ?? lista.find((x) => x.marca === marca);
  if (scris) return scris;
  if (lista.some((x) => x.marca != null || (x.marci?.length ?? 0) > 0)) return null;
  return lista.find((x) => String(Number(x.tip)) === c.tip) ?? null;
}

/** Pozițiile mărcii, grupate pe element, în ordinea greutății: cele ale
 *  tipului `tip` (toate utilizările lui) când rândul de tip e știut; altfel
 *  cele care poartă chiar marca, iar pe un extras fără mărci scrise cele cu
 *  `tip` = numărul mărcii. */
export function elementeleMarcii(pozitii: PozitieExtras[], marca: string, tip?: string | null):
    { element: string; nivel: string; n: number; kg: number; roluri: string[] }[] {
  const c = citesteMarca(marca);
  if (!c) return [];
  const cuMarci = pozitii.some((p) => p.marca != null);
  const aMarcii = (p: PozitieExtras) => {
    if (tip != null) return p.tip != null && String(p.tip) === String(tip);
    if (cuMarci) return p.marca === marca;
    return p.tip != null && String(Number(p.tip)) === c.tip;
  };
  const pe = new Map<string, { element: string; nivel: string; n: number; kg: number; roluri: string[] }>();
  for (const p of pozitii) {
    if (!aMarcii(p)) continue;
    const g = pe.get(p.element) ?? {
      element: p.element, nivel: String(p.nivel_nume ?? p.nivel ?? ""), n: 0, kg: 0, roluri: [],
    };
    g.n += p.n ?? 0;
    g.kg += p.kg ?? 0;
    if (p.rol && !g.roluri.includes(p.rol)) g.roluri.push(p.rol);
    pe.set(p.element, g);
  }
  return [...pe.values()].sort((a, b) => b.kg - a.kg);
}

export default function MarcaArmaturaPage({ id, cloneId, marca, solutie }: {
  id: string;
  cloneId?: string | null;
  marca: string;
  solutie?: string | null;
}) {
  const t = useT();
  const citita = citesteMarca(marca);
  const categorie = categoriaMarcii(marca);
  const [tip, setTip] = useState<TipBara | null | undefined>(undefined);
  const [pozitii, setPozitii] = useState<PozitieExtras[] | null | undefined>(undefined);
  const [versiune, setVersiune] = useState<string | null>(null);
  // `undefined` = pe drum; `null` = forma nu e desenată încă (404)
  const [forma, setForma] = useState<DrawingData | null | undefined>(undefined);

  useEffect(() => {
    if (cloneId == null || !categorie) { setTip(null); setPozitii(null); return; }
    let viu = true;
    setTip(undefined);
    setPozitii(undefined);
    getEstimare(id, cloneId)
      .then((e) => { if (viu) setTip(tipulMarcii(e.componente?.[categorie], marca)); })
      .catch(() => { if (viu) setTip(null); });
    getExtrasArmare(id, { categorie }, cloneId)
      .then((x) => { if (viu) setPozitii(x.pozitii ?? []); })
      .catch(() => { if (viu) setPozitii(null); });
    getPlanArmareColi(id, cloneId)
      .then((c) => { if (viu) setVersiune(c.versiune); })
      .catch(() => undefined);
    return () => { viu = false; };
  }, [id, cloneId, marca, categorie]);

  const src = cloneId != null && citita ? planArmareMarcaUrl(id, cloneId, marca) : null;
  useEffect(() => {
    if (!src) { setForma(null); return; }
    let viu = true;
    setForma(undefined);
    fetchDrawingUrl(src, versiune)
      .then((d) => { if (viu) setForma(d); })
      .catch(() => { if (viu) setForma(null); });
    return () => { viu = false; };
  }, [src, versiune]);

  const elemente = useMemo(
    () => elementeleMarcii(pozitii ?? [], marca, tip?.tip), [pozitii, marca, tip]);
  const kgTotal = elemente.reduce((s, e) => s + e.kg, 0);
  const bucati = elemente.reduce((s, e) => s + e.n, 0);

  const linkComponente = hashComponente(id, solutie ?? null, categorie ?? "bare");

  return (
    <div className="ob-pagina" style={{ maxWidth: 1180, margin: "0 auto" }} data-testid="marca-pagina">
      <div className="ob-pas-antet" style={{ paddingTop: 0 }}>
        <a href={hashSectiune(id, "acasa", solutie ?? null)}>{t("struct.vp.disciplina")}</a>
        <span>/</span>
        <a href={linkComponente}>{t("struct.comp.titlu")}</a>
        <span>/</span>
        <span>{citita ? t(NUME_FAMILIE_MARCA[citita.familie]) : t("struct.pa.marca_necunoscuta")}</span>
        <span>/</span>
        <b style={{ color: "var(--ink)" }}>{marca}</b>
      </div>

      {!citita ? (
        <div className="ob-card" style={{ padding: 14 }}>
          <b>{t("struct.pa.marca_necunoscuta")}</b>
          <div className="sg-muted" style={{ marginTop: 6 }}>
            {t("struct.pa.marca_necunoscuta_nota", { marca })}
          </div>
        </div>
      ) : (
        <>
          {/* cartea de identitate a tipului */}
          <div className="ob-card" style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <b className="sg-mono" style={{ fontSize: 22 }}>{marca}</b>
              <span className="ob-chip ob-c-acc">{t(NUME_FAMILIE_MARCA[citita.familie])}</span>
              {tip && <span className="ob-chip ob-c-mut">Ø{nr(tip.phi_mm)}</span>}
              {tip?.dim_cm && tip.dim_cm.length > 0 && (
                <span className="ob-chip ob-c-mut">{tip.dim_cm.map((d) => nr(d, 1)).join(" × ")} cm</span>
              )}
              <span style={{ flex: 1 }} />
              {tip && (
                <span className="sg-muted" style={{ fontSize: 12 }}>
                  {t("struct.pa.marca_tip", { tip: tip.tip })}
                </span>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              {tip === undefined ? (
                <span className="sg-muted">{t("comun.se_incarca")}</span>
              ) : tip ? (
                <>
                  <div>{tip.descriere}</div>
                  <div className="sg-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {t("struct.pa.marca_cifre", {
                      nr: nr(tip.nr), elemente: nr(tip.elemente),
                      kg: nr(tip.kg), m: nr(tip.l_tot_m),
                    })}
                  </div>
                </>
              ) : (
                <span className="sg-muted">{t("struct.pa.marca_fara_tip")}</span>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            {/* forma desenată, cotată */}
            <div className="ob-card" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <div style={{ padding: "11px 14px" }}>
                <b>{t("struct.pa.forma_titlu")}</b>
              </div>
              {forma === undefined ? (
                <div className="sg-muted" style={{ padding: "0 14px 12px", fontSize: 12 }}>
                  {t("comun.se_incarca")}
                </div>
              ) : forma === null || !src ? (
                <div className="sg-muted" style={{ padding: "0 14px 14px", fontSize: 12 }}
                     data-testid="forma-lipsa">
                  {t("struct.pa.forma_lipsa")}
                </div>
              ) : (
                <div style={{ height: 360, borderTop: "1px solid var(--line)" }}>
                  <DrawingView src={src} versiune={versiune}
                               title={t("struct.pa.forma_desen", { marca })} embed />
                </div>
              )}
            </div>

            {/* unde se folosește */}
            <div className="ob-card" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
                 data-testid="marca-elemente">
              <div style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <b>{t("struct.pa.unde_titlu")}</b>
                {pozitii && elemente.length > 0 && (
                  <span className="ob-acasa-sub">
                    {t("struct.pa.unde_sub", {
                      elemente: nr(elemente.length), buc: nr(bucati), kg: nr(kgTotal),
                    })}
                  </span>
                )}
              </div>
              {pozitii === undefined ? (
                <div className="sg-muted" style={{ padding: "0 14px 12px", fontSize: 12 }}>
                  {t("comun.se_incarca")}
                </div>
              ) : !elemente.length ? (
                <div className="sg-muted" style={{ padding: "0 14px 12px", fontSize: 12 }}>
                  {t("struct.pa.unde_gol")}
                </div>
              ) : (
                <table className="ob-sol-tabel">
                  <thead>
                    <tr>
                      <th>{t("struct.pa.col_element")}</th>
                      <th>{t("struct.pa.col_nivel")}</th>
                      <th>{t("struct.pa.col_rol")}</th>
                      <th style={{ width: 60, textAlign: "right" }}>{t("struct.inv.col_buc")}</th>
                      <th style={{ width: 70, textAlign: "right" }}>{t("struct.inv.col_kg")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {elemente.map((e) => (
                      <tr key={e.element}>
                        <td className="sg-mono" style={{ fontSize: 12 }}>
                          <a href={hashElement(id, e.element, solutie)}>{e.element}</a>
                        </td>
                        <td style={{ fontSize: 12 }}>{e.nivel}</td>
                        <td style={{ fontSize: 12 }}>{e.roluri.join(" · ")}</td>
                        <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>{nr(e.n)}×</td>
                        <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>{nr(e.kg, 1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
